// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { Modal, Pressable, StyleSheet, Text, View } from 'react-native';

import { useUser } from '@/app/user-context';
import { useI18n } from '@/i18n';
import { requestBugReport } from '@/services/bugReportRequest';
import { useBetaInfoState } from '@/stores/betaInfoState';
import appColors from '@/theme/colors';

/**
 * Explainer behind the beta badge. Mounted once in RootLayoutNav; every badge opens it through
 * the betaInfoState store rather than through props, because the badges render in three separate
 * trees.
 */
export function BetaInfoModal() {
  const { t } = useI18n();
  const { user } = useUser();
  const open = useBetaInfoState((state) => state.open);
  const setOpen = useBetaInfoState((state) => state.setOpen);
  // The bug-report form posts over the authenticated BFF socket, and its other entry points (left
  // menu, account menu) are signed-in only. Offering it to a signed-out visitor on the start
  // screen would open a form that cannot be delivered.
  const canReportBug = Boolean(user?.token);

  const close = () => setOpen(false);

  return (
    <Modal transparent visible={open} animationType="fade" onRequestClose={close}>
      <Pressable style={styles.backdrop} onPress={close}>
        {/* Swallow presses on the card so they do not dismiss through the backdrop. */}
        <Pressable style={styles.card} testID="beta-info-modal" onPress={() => undefined}>
          <Text style={styles.title}>{t('beta.title')}</Text>
          <Text style={styles.body}>{t('beta.body')}</Text>
          {canReportBug ? (
            <>
              <Text style={styles.body}>{t('beta.feedback')}</Text>
              <Pressable
                style={styles.reportButton}
                testID="beta-info-report-bug"
                onPress={() => {
                  // Close first: two stacked native modals fight for the window on iOS.
                  close();
                  requestBugReport();
                }}
              >
                <Text style={styles.reportButtonText}>{t('menu.reportBug')}</Text>
              </Pressable>
            </>
          ) : null}
          <Pressable style={styles.close} testID="beta-info-close" onPress={close}>
            <Text style={styles.closeText}>{t('menu.close')}</Text>
          </Pressable>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(17,44,27,0.14)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  card: {
    minWidth: 260,
    maxWidth: 380,
    padding: 18,
    borderRadius: 12,
    backgroundColor: '#07200D',
    borderWidth: 1,
    borderColor: '#A1D493',
    shadowColor: '#C8C8B0',
    shadowOpacity: 0.35,
    shadowRadius: 12,
    shadowOffset: { width: 0, height: 6 },
    elevation: 8,
  },
  title: {
    color: '#f4f6fb',
    fontSize: 16,
    fontWeight: '700',
    marginBottom: 10,
  },
  body: {
    color: '#d6e4d2',
    fontSize: 14,
    lineHeight: 20,
    marginBottom: 12,
  },
  reportButton: {
    alignSelf: 'flex-start',
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 8,
    marginBottom: 12,
    backgroundColor: appColors.button.primaryBg,
  },
  reportButtonText: {
    color: appColors.button.primaryText,
    fontWeight: '600',
  },
  close: {
    alignSelf: 'flex-end',
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 8,
    backgroundColor: '#C8C8B0',
  },
  closeText: {
    color: appColors.text.primary,
    fontWeight: '600',
  },
});
